import React, { useState, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, ListGroup, Alert } from 'react-bootstrap';
import { PDFDocument } from 'pdf-lib';
import { Helmet } from 'react-helmet';
import '../../styles/PDFMerger.css';

const PDFMerger = () => {
  const [pdfFiles, setPdfFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    // Filter only PDF files
    const pdfFilesToAdd = files.filter(file => file.type === 'application/pdf');
    
    // Check if we're exceeding the max limit of 5
    if (pdfFiles.length + pdfFilesToAdd.length > 5) {
      setError('You can only upload up to 5 PDF files.');
      return;
    }

    // Reset states
    setError('');
    setSuccess(false);
    
    // Add new files to the state with preview URLs
    const newPdfFiles = pdfFilesToAdd.map(file => ({
      file,
      name: file.name,
      size: (file.size / 1024 / 1024).toFixed(2), // Size in MB
    }));

    setPdfFiles([...pdfFiles, ...newPdfFiles]);
    
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    const updatedFiles = [...pdfFiles];
    updatedFiles.splice(index, 1);
    setPdfFiles(updatedFiles);
    setSuccess(false);
  };

  const moveFileUp = (index) => {
    if (index === 0) return;
    const updatedFiles = [...pdfFiles];
    [updatedFiles[index - 1], updatedFiles[index]] = [updatedFiles[index], updatedFiles[index - 1]];
    setPdfFiles(updatedFiles);
  };

  const moveFileDown = (index) => {
    if (index === pdfFiles.length - 1) return;
    const updatedFiles = [...pdfFiles];
    [updatedFiles[index], updatedFiles[index + 1]] = [updatedFiles[index + 1], updatedFiles[index]];
    setPdfFiles(updatedFiles);
  };

  const mergePDFs = async () => {
    if (pdfFiles.length === 0) {
      setError('Please add at least one PDF file.');
      return;
    }
    
    if (pdfFiles.length === 1) {
      setError('Need at least two PDF files to merge.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Create a new PDF document
      const mergedPdf = await PDFDocument.create();
      
      // Loop through all the PDF files and add their pages to the new PDF
      for (const pdfFile of pdfFiles) {
        const fileData = await pdfFile.file.arrayBuffer();
        const pdf = await PDFDocument.load(fileData);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => {
          mergedPdf.addPage(page);
        });
      }
      
      // Serialize the merged PDF to bytes
      const mergedPdfBytes = await mergedPdf.save();
      
      // Create a Blob from the bytes
      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      
      // Create a URL for the Blob
      const url = URL.createObjectURL(blob);
      
      // Create a temporary link and trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.download = 'merged-document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess(true);
    } catch (err) {
      console.error('Error merging PDFs:', err);
      setError('Failed to merge PDFs. Please try again with valid PDF files.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="page-container pdf-merger-container">
      <Helmet>
        <title>PDF Merger Tool - Combine Multiple PDFs Online | MicroGenie</title>
        <meta name="description" content="Merge multiple PDF files into a single document for free. Upload up to 5 PDFs, rearrange their order, and combine them with our easy-to-use PDF merger tool." />
        <meta name="keywords" content="pdf merger, combine pdf, merge pdf files, pdf joiner, free pdf tools, online pdf merger, pdf combiner" />
        <meta property="og:title" content="PDF Merger - Combine Multiple PDFs Online | MicroGenie" />
        <meta property="og:description" content="Merge multiple PDF files into a single document with our free online PDF merger tool. No registration required." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PDF Merger Tool - Combine Multiple PDFs Online | MicroGenie" />
        <meta name="twitter:description" content="Merge multiple PDF files into a single document for free. Upload up to 5 PDFs, rearrange their order, and combine them with our easy-to-use PDF merger tool." />
      </Helmet>

      <Row className="justify-content-center mb-5">
        <Col lg={10}>
          <div className="text-center">
            <h1 className="section-title">PDF Merger Tool</h1>
            <p className="lead mb-4">
              Merge multiple PDF files into a single document. Upload up to 5 PDF files, arrange them in your desired order, and combine with one click.
            </p>
          </div>
        </Col>
      </Row>

      <Row className="justify-content-center">
        <Col lg={10}>
          <Card className="pdf-merger-card">
            <Card.Body>
              <div className="file-upload-section">
                <div className="upload-area text-center p-5">
                  <i className="la la-file-pdf mb-3" style={{ fontSize: '48px' }} aria-hidden="true"></i>
                  <h3 className="mb-3">Upload PDF Files</h3>
                  <p className="text-muted mb-4">Upload up to 5 PDF files (max 10MB each)</p>
                  
                  <div className="d-grid gap-2">
                    <Form.Group controlId="formFileMultiple">
                      <Form.Control 
                        type="file" 
                        accept="application/pdf" 
                        multiple 
                        onChange={handleFileChange} 
                        ref={fileInputRef}
                        className="d-none"
                      />
                      <Button 
                        variant="primary" 
                        className="upload-btn" 
                        onClick={() => fileInputRef.current.click()}
                      >
                        <i className="la la-upload me-2" aria-hidden="true"></i> Choose PDF Files
                      </Button>
                    </Form.Group>
                  </div>
                </div>
              </div>

              {error && <Alert variant="danger" className="mt-4">{error}</Alert>}
              {success && <Alert variant="success" className="mt-4">PDFs have been merged successfully!</Alert>}

              {pdfFiles.length > 0 && (
                <div className="pdf-list-section mt-5">
                  <h4 className="mb-4">Selected PDF Files ({pdfFiles.length}/5)</h4>
                  <ListGroup className="pdf-files-list">
                    {pdfFiles.map((file, index) => (
                      <ListGroup.Item key={index} className="pdf-file-item">
                        <div className="d-flex align-items-center">
                          <div className="me-2 file-number">{index + 1}.</div>
                          <i className="la la-file-pdf me-3 text-danger" style={{ fontSize: '24px' }} aria-hidden="true"></i>
                          <div className="file-info flex-grow-1">
                            <div className="file-name">{file.name}</div>
                            <div className="file-size text-muted small">{file.size} MB</div>
                          </div>
                          <div className="file-actions">
                            <Button 
                              variant="light" 
                              className="btn-sm me-1" 
                              disabled={index === 0}
                              onClick={() => moveFileUp(index)}
                            >
                              <i className="la la-arrow-up" aria-hidden="true"></i>
                            </Button>
                            <Button 
                              variant="light" 
                              className="btn-sm me-1" 
                              disabled={index === pdfFiles.length - 1}
                              onClick={() => moveFileDown(index)}
                            >
                              <i className="la la-arrow-down" aria-hidden="true"></i>
                            </Button>
                            <Button 
                              variant="light" 
                              className="btn-sm text-danger"
                              onClick={() => removeFile(index)}
                            >
                              <i className="la la-times" aria-hidden="true"></i>
                            </Button>
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}

              {pdfFiles.length > 0 && (
                <div className="action-buttons mt-4 text-center">
                  <Button 
                    variant="success" 
                    className="merge-btn" 
                    onClick={mergePDFs}
                    disabled={loading || pdfFiles.length < 2}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Merging...
                      </>
                    ) : (
                      <>
                        <i className="la la-object-group me-2" aria-hidden="true"></i> Merge PDFs
                      </>
                    )}
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>

          <div className="pdf-info mt-5">
            <h4>About Our PDF Merger Tool</h4>
            <p>
              Our PDF Merger Tool allows you to combine multiple PDF files into a single document quickly and easily. 
              This free online tool works directly in your browser, ensuring that your files remain secure and private.
            </p>
            
            <h5 className="mt-4">How to use the PDF Merger Tool:</h5>
            <ol>
              <li>Click the "Choose PDF Files" button and select up to 5 PDF files you want to merge.</li>
              <li>Arrange the files in your desired order using the up and down arrows.</li>
              <li>Click the "Merge PDFs" button to combine the files.</li>
              <li>Download your merged PDF document.</li>
            </ol>
            
            <div className="security-info mt-4">
              <h5>Privacy & Security:</h5>
              <p>
                Your PDF files are processed entirely in your browser and are not uploaded to our servers. 
                This ensures complete privacy and security for your documents.
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default PDFMerger;
